import { gql, useMutation, useQuery } from '@apollo/client';
import { CvlFinancialQualificationCriteriaEnumType, DocumentTypeEnum } from '__generated__/graphql';
import { FC, useMemo } from 'react';

import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';

type Props = {
  qualificationCriteria?: CvlFinancialQualificationCriteriaEnumType | '';
  selectedInvestmentEntity: any | null;
  onSuccess?: () => void;
  onBack?: () => void;
  locked?: boolean;
};

const CRITERIA_LABELS: Record<string, string> = {
  [CvlFinancialQualificationCriteriaEnumType.AnnualIncome]: 'Annual Income',
  [CvlFinancialQualificationCriteriaEnumType.NetWorth]: 'Net Worth',
  [CvlFinancialQualificationCriteriaEnumType.Combined]: 'Combined - Net Worth & Income',
};

const CVL_INVESTOR_APPLICATION_ID_QUERY = gql(`
  query InvestorAccreditationApplicationIdForDocumentDetails($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      id
      validityPeriodOfAccreditation
      documents {
        id
        documentType
        documentName
        documentUrl
        createdAt
      }
    }
  }
`);

export const ADD_DOCUMENT_DETAILS_MUTATION = gql(`
  mutation AddDocumentDetails(
    $investorAccreditationApplicationId: ID!
    $panDocument: Upload
    $identityProofDocument: Upload
    $itrAcknowledgementDocumentYear1: Upload
    $itrAcknowledgementDocumentYear2: Upload
    $netWorthCertificateDocumentYear1: Upload
    $netWorthCertificateDocumentYear2: Upload
    $undertakingDocument: Upload
  ) {
    addDocumentDetails(
      investorAccreditationApplicationId: $investorAccreditationApplicationId
      panDocument: $panDocument
      identityProofDocument: $identityProofDocument
      itrAcknowledgementDocumentYear1: $itrAcknowledgementDocumentYear1
      itrAcknowledgementDocumentYear2: $itrAcknowledgementDocumentYear2
      netWorthCertificateDocumentYear1: $netWorthCertificateDocumentYear1
      netWorthCertificateDocumentYear2: $netWorthCertificateDocumentYear2
      undertakingDocument: $undertakingDocument
    ) {
      id
    }
  }
`);

const Documents: FC<Props> = ({
  qualificationCriteria = '',
  selectedInvestmentEntity,
  onSuccess,
  onBack,
  locked,
}) => {
  const netWorthSampleUrl =
    qualificationCriteria === CvlFinancialQualificationCriteriaEnumType.Combined
      ? '/samples/sample-net-worth-certificate-combined.pdf'
      : '/samples/sample-net-worth-certificate-individual.pdf';

  const { data: cvlData } = useQuery(CVL_INVESTOR_APPLICATION_ID_QUERY, {
    variables: { investmentEntityId: selectedInvestmentEntity?.id },
    skip: !selectedInvestmentEntity?.id,
    fetchPolicy: 'network-only',
  });

  const rawValidity = cvlData?.investorAccreditationApplication?.validityPeriodOfAccreditation;
  const validityCode =
    typeof rawValidity === 'number'
      ? rawValidity
      : rawValidity != null
      ? Number(rawValidity)
      : undefined;
  // Backend requires ITR acknowledgement and net worth certificate for one
  // additional year (FY 24-25) when the investor opted for 3-year validity.
  const isThreeYearValidity = validityCode === 2;

  const sortedDocuments = useMemo(
    () =>
      (cvlData?.investorAccreditationApplication?.documents &&
      Array.isArray(cvlData.investorAccreditationApplication.documents)
        ? cvlData.investorAccreditationApplication.documents.filter(Boolean)
        : []
      ).sort((a, b) => {
        const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return aTime - bTime;
      }),
    [cvlData?.investorAccreditationApplication?.documents]
  );

  const getDocumentValueForType = (documentType: DocumentTypeEnum) => {
    const doc = sortedDocuments.find(d => d?.documentType === documentType);
    if (!doc) return undefined;

    return {
      name: doc.documentName,
      url: doc.documentUrl,
    };
  };

  const [addDocumentDetails, { loading, error }] = useMutation(ADD_DOCUMENT_DETAILS_MUTATION);

  const handleSubmit = (data: any) => {
    const toUpload = (value: unknown) => {
      if (value instanceof File) return value;
      return undefined;
    };

    const cvlApplicationId = cvlData?.investorAccreditationApplication?.id;

    if (!selectedInvestmentEntity?.id || !cvlApplicationId) {
      // eslint-disable-next-line no-console
      console.error(
        'Missing investment entity or CVL investor application ID when adding document details'
      );
      return;
    }

    return addDocumentDetails({
      variables: {
        investorAccreditationApplicationId: cvlApplicationId,
        panDocument: toUpload(data.panCard),
        identityProofDocument: toUpload(data.identityProof),
        itrAcknowledgementDocumentYear1: toUpload(data.annualIncomeProof),
        itrAcknowledgementDocumentYear2: isThreeYearValidity
          ? toUpload(data.annualIncomeProofYear2)
          : undefined,
        netWorthCertificateDocumentYear1: toUpload(data.netWorthCertificate),
        netWorthCertificateDocumentYear2: isThreeYearValidity
          ? toUpload(data.netWorthCertificateYear2)
          : undefined,
        undertakingDocument: toUpload(data.undertakingDocument),
      },
    }).then(() => {
      if (onSuccess) {
        onSuccess();
      }
    });
  };

  return (
    <div>
      <p className="mb-4 text-sm text-gray-600">
        Please upload the required documents to proceed with your accreditation application.
      </p>
      {qualificationCriteria && (
        <div className="mb-4 inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-900">
          <span className="text-indigo-700">Qualification criteria</span>
          <span className="mx-2 text-indigo-300">•</span>
          <span>{CRITERIA_LABELS[qualificationCriteria] ?? qualificationCriteria}</span>
        </div>
      )}
      {locked ? (
        <FormPanelWithReadMode
          key={`${qualificationCriteria}-${sortedDocuments.map(doc => doc?.id).join('|')}`}
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          submitButtonLabel="Save"
          disableEdit={true}
        >
          <FormInput
            fieldName="qualificationCriteria"
            type="text"
            label=""
            defaultValue={qualificationCriteria}
            hidden
          />
          <FormInput
            fieldName="panCard"
            label="PAN Card"
            type="upload"
            helperNode={
              <>
                Upload a clear <strong>self attested</strong> copy of your PAN card.
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.Pan)}
            validators={{ required: true }}
            maxFileSize={5}
            onlyAcceptsPDF={true}
          />
          <FormInput
            fieldName="identityProof"
            label="Identity Proof Document"
            type="upload"
            helperNode={
              <>
                Upload a clear <strong>self attested</strong> copy of your Aadhaar card / Passport /
                Driving License / Voter ID.
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.IdentityProof)}
            validators={{ required: true }}
            maxFileSize={5}
            onlyAcceptsPDF={true}
          />
          <FormInput
            fieldName="annualIncomeProof"
            label="ITR / ITR Acknowledgement for FY 2025-26"
            type="upload"
            helperText="Upload your ITR or ITR acknowledgement for FY 2025-26."
            defaultValue={getDocumentValueForType(DocumentTypeEnum.ItrAcknowledgementYear_1)}
            validators={{ required: true }}
            maxFileSize={10}
            onlyAcceptsPDF={true}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="netWorthCertificate"
            label="Net Worth Certificate for FY 2025-26"
            type="upload"
            helperNode={
              <>
                Upload your net worth certificate for the preceding financial year.{' '}
                <a
                  href={netWorthSampleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Download sample net worth certificate
                </a>
                .
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.NetWorthCertificateYear_1)}
            validators={{ required: true }}
            maxFileSize={10}
            onlyAcceptsPDF={true}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <div
            className={
              isThreeYearValidity
                ? 'col-span-full mt-2 mb-1 text-sm font-medium text-indigo-700'
                : 'hidden'
            }
          >
            A 3-year validity requires documents for one additional year: FY 24-25.
          </div>
          <FormInput
            fieldName="annualIncomeProofYear2"
            label="ITR / ITR Acknowledgement for FY 2024-25"
            type="upload"
            helperText="Upload your ITR or ITR acknowledgement for FY 2024-25."
            defaultValue={getDocumentValueForType(DocumentTypeEnum.ItrAcknowledgementYear_2)}
            hidden={!isThreeYearValidity}
            validators={{ required: true }}
            maxFileSize={10}
            onlyAcceptsPDF={true}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="netWorthCertificateYear2"
            label="Net Worth Certificate for FY 2024-25"
            type="upload"
            helperNode={
              <>
                Upload your net worth certificate for FY 2024-25.{' '}
                <a
                  href={netWorthSampleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Download sample net worth certificate
                </a>
                .
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.NetWorthCertificateYear_2)}
            hidden={!isThreeYearValidity}
            validators={{ required: true }}
            maxFileSize={10}
            onlyAcceptsPDF={true}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="undertakingDocument"
            label="Undertaking"
            type="upload"
            helperNode={
              <>
                Upload the signed undertaking document.{' '}
                <a
                  href="/samples/undertaking-sole-individual.docx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Download sample undertaking
                </a>
                .
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.Undertaking)}
            validators={{ required: true }}
            maxFileSize={5}
            onlyAcceptsPDF={true}
          />
        </FormPanelWithReadMode>
      ) : (
        <FormPanel
          key={`${qualificationCriteria}-${sortedDocuments.map(doc => doc?.id).join('|')}`}
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          submitButtonLabel="Save"
          onCancel={onBack}
          cancelButtonLabel="Previous step"
          cancelButtonFirst
        >
          <FormInput
            fieldName="qualificationCriteria"
            type="text"
            label=""
            defaultValue={qualificationCriteria}
            hidden
          />
          <FormInput
            fieldName="panCard"
            label="PAN Card"
            type="upload"
            helperNode={
              <>
                Upload a clear <strong>self attested</strong> copy of your PAN card.
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.Pan)}
            validators={{ required: true }}
            maxFileSize={5}
            onlyAcceptsPDF={true}
          />
          <FormInput
            fieldName="identityProof"
            label="Identity Proof Document"
            type="upload"
            helperNode={
              <>
                Upload a clear <strong>self attested</strong> copy of your Aadhaar card / Passport /
                Driving License / Voter ID.
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.IdentityProof)}
            validators={{ required: true }}
            maxFileSize={5}
            onlyAcceptsPDF={true}
          />
          <FormInput
            fieldName="annualIncomeProof"
            label="ITR / ITR Acknowledgement for FY 2025-26"
            type="upload"
            helperText="Upload your ITR or ITR acknowledgement for FY 2025-26."
            defaultValue={getDocumentValueForType(DocumentTypeEnum.ItrAcknowledgementYear_1)}
            validators={{ required: true }}
            maxFileSize={10}
            onlyAcceptsPDF={true}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="netWorthCertificate"
            label="Net Worth Certificate for FY 2025-26"
            type="upload"
            helperNode={
              <>
                Upload your net worth certificate for the preceding financial year.{' '}
                <a
                  href={netWorthSampleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Download sample net worth certificate
                </a>
                .
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.NetWorthCertificateYear_1)}
            validators={{ required: true }}
            maxFileSize={10}
            onlyAcceptsPDF={true}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <div
            className={
              isThreeYearValidity
                ? 'col-span-full mt-2 mb-1 text-sm font-medium text-indigo-700'
                : 'hidden'
            }
          >
            A 3-year validity requires documents for one additional year: FY 24-25.
          </div>
          <FormInput
            fieldName="annualIncomeProofYear2"
            label="ITR / ITR Acknowledgement for FY 2024-25"
            type="upload"
            helperText="Upload your ITR or ITR acknowledgement for FY 2024-25."
            defaultValue={getDocumentValueForType(DocumentTypeEnum.ItrAcknowledgementYear_2)}
            hidden={!isThreeYearValidity}
            validators={{ required: true }}
            maxFileSize={10}
            onlyAcceptsPDF={true}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="netWorthCertificateYear2"
            label="Net Worth Certificate for FY 2024-25"
            type="upload"
            helperNode={
              <>
                Upload your net worth certificate for FY 2024-25.{' '}
                <a
                  href={netWorthSampleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Download sample net worth certificate
                </a>
                .
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.NetWorthCertificateYear_2)}
            hidden={!isThreeYearValidity}
            validators={{ required: true }}
            maxFileSize={10}
            onlyAcceptsPDF={true}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="undertakingDocument"
            label="Undertaking"
            type="upload"
            helperNode={
              <>
                Upload the signed undertaking document.{' '}
                <a
                  href="/samples/undertaking-sole-individual.docx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Download sample undertaking
                </a>
                .
              </>
            }
            defaultValue={getDocumentValueForType(DocumentTypeEnum.Undertaking)}
            validators={{ required: true }}
            maxFileSize={5}
            onlyAcceptsPDF={true}
          />
        </FormPanel>
      )}
    </div>
  );
};

export default Documents;
